/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description TogglePayerStatus Button
 */
import { Button, Dropdown, Tooltip, App, ButtonProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MoreOne, Redo } from '@icon-park/react';
import _ from 'lodash';

import { MODAL_TYPES } from 'components/widgets/modal';
import { useAppStoreValues } from 'store/app';
import useUpdateSelectedPayers from 'hooks/admin/mutate/useUpdateSelectedPayers';
import { getManagePayersPath } from 'utils/router';
import { DISABLE_PAYER_FIELDS } from 'app-constants/admin/payer';
import { ELIGIBILITY_PAYER_STATUS_CODES } from 'app-constants/eligibility';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';
/**
 * TogglePayerStatus
 *
 */
const TogglePayerStatus: React.FC<Record<'payer', Payers.ISelectedPayer> & ButtonProps> = ({ payer, ...props }) => {
	const { modal } = App.useApp();
	const navigate = useNavigate();
	const { mutateAsync, isPending: isLoading } = useUpdateSelectedPayers();
	const { canUpdatePayers } = useAppStoreValues().userAccess;
	const isArchived = payer?.status === ELIGIBILITY_PAYER_STATUS_CODES.archived;
	const isActive = payer?.status === ELIGIBILITY_PAYER_STATUS_CODES.active;
	const isPending = payer?.status === ELIGIBILITY_PAYER_STATUS_CODES.pending;
	const toDeactivate = isActive || isPending;

	const changePayerStatus = () => {
		modal.confirm({
			...MODAL_TYPES.confirm,
			width: 560,
			title: toDeactivate ? 'Deactivate Payer' : MODAL_TYPES.confirm.title,
			content: toDeactivate ? (
				<>
					{'Are you sure you want to deactivate this payer for all providers in your organization?'}
					<br />
					{'Deactivating this payer will disable it across the organization.'}
				</>
			) : (
				'Are you sure you want to activate this payer for all providers of your practice?'
			),
			okText: toDeactivate ? 'Deactivate' : 'Yes',
			okButtonProps: toDeactivate ? { danger: true } : undefined,
			cancelText: 'Cancel',
			onOk: () =>
				mutateAsync(
					[
						{
							id: payer.id,
							status: toDeactivate ? ELIGIBILITY_PAYER_STATUS_CODES.inactive : ELIGIBILITY_PAYER_STATUS_CODES.active,
						},
					],
					{
						onSuccess: () => {
							// redirect to payers as deactivated payer will be not exist
							if (toDeactivate) {
								navigate(getManagePayersPath());
							}
						},
					},
				),
		});
	};
	if (toDeactivate) {
		return (
			<Tooltip title={`${canUpdatePayers ? '' : NO_ACCESS_TOOLTIP_TEXT}`}>
				<Dropdown
					menu={{
						items: [
							{
								key: DISABLE_PAYER_FIELDS.deactivate.key,
								label: DISABLE_PAYER_FIELDS.deactivate.label,
								danger: DISABLE_PAYER_FIELDS.deactivate.danger,
								onClick: () => changePayerStatus(),
							},
						],
					}}
					disabled={isLoading || !canUpdatePayers}
					placement='bottomRight'
				>
					{/* TODO: Add action button css */}
					<Button {...props} icon={<MoreOne theme='filled' size='22' />} />
				</Dropdown>
			</Tooltip>
		);
	}

	if (!_.isNil(payer) && !isArchived) {
		return (
			<Tooltip title={`${canUpdatePayers ? 'Activate Payer' : NO_ACCESS_TOOLTIP_TEXT}`}>
				<Button
					{...props}
					color='default'
					icon={<Redo />}
					disabled={isLoading || !canUpdatePayers}
					onClick={() => changePayerStatus()}
				/>
			</Tooltip>
		);
	}

	return null;
};

export default TogglePayerStatus;
